<template>
  <b-overlay :show="loading" rounded="sm" no-fade>
    <b-card class="governance-reports">
      <b-row class="mb-1 align-items-center">
        <b-col cols="12" md="3" class="mb-1 mb-md-0">
          <h4 class="mb-0">Governance Reports</h4>
        </b-col>
        <b-col cols="12" md="9" class="d-flex align-items-center justify-content-end flex-wrap">
          <flat-pickr
            v-model="dateRange"
            class="form-control mr-1 mb-50"
            style="max-width: 230px;"
            :config="rangeConfig"
            placeholder="Period"
            @on-change="onRangeChange"
          />
          <v-select
            v-model="filters.event_type"
            :options="typeOptions"
            :reduce="(o) => o.value"
            placeholder="Type"
            class="mr-1 mb-50"
            style="min-width: 160px;"
            @input="load"
          />
          <v-select
            v-model="filters.working_group_id"
            :options="workingGroups"
            label="group_name"
            :reduce="(o) => o.id"
            placeholder="Working group"
            class="mr-1 mb-50"
            style="min-width: 200px;"
            @input="load"
          />
          <b-button variant="outline-primary" size="sm" class="mb-50" @click="exportFile('downloadPdf', 'governance_report.pdf')">
            <feather-icon icon="DownloadIcon" class="mr-25" />Full report (PDF)
          </b-button>
        </b-col>
      </b-row>

      <b-tabs v-if="summary && analytics" v-model="activeTab" pills fill class="governance-report-tabs">
        <!-- Overview -->
        <b-tab title="Overview">
          <div class="d-flex justify-content-end mb-1">
            <b-button variant="outline-success" size="sm" class="mr-50" @click="exportFile('exportEvents', 'governance_events.xlsx')">
              <feather-icon icon="FileTextIcon" class="mr-25" />Events (Excel)
            </b-button>
            <b-button variant="outline-success" size="sm" @click="exportFile('exportInvitees', 'governance_event_invitees.xlsx')">
              <feather-icon icon="UsersIcon" class="mr-25" />Invitees (Excel)
            </b-button>
          </div>
          <b-row class="mb-2">
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Total events" :value="summary.totals.events" icon="CalendarIcon" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Held" :value="summary.totals.held" icon="CheckCircleIcon" hint="Completed, or scheduled and already past" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Upcoming" :value="summary.totals.scheduled" icon="ClockIcon" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Cancelled" :value="summary.totals.cancelled" icon="XCircleIcon" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Response rate" :value="pct(analytics.responses.overall.response_rate)" icon="MessageSquareIcon" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Minutes compliance" :value="pct(analytics.compliance.totals.compliance_rate)" icon="FileTextIcon" hint="Held meetings with both agenda and minutes" /></b-col>
          </b-row>

          <b-row>
            <b-col cols="12" lg="4" class="mb-2">
              <h6>By type</h6>
              <b-table :items="summary.by_type" :fields="typeFields" small striped responsive show-empty empty-text="No events" />
            </b-col>
            <b-col cols="12" lg="4" class="mb-2">
              <h6>By month</h6>
              <b-table :items="summary.by_month" :fields="monthFields" small striped responsive show-empty empty-text="No events" />
            </b-col>
            <b-col cols="12" lg="4" class="mb-2">
              <h6>By working group</h6>
              <b-table :items="summary.by_working_group" :fields="wgFields" small striped responsive show-empty empty-text="No events" />
            </b-col>
          </b-row>

          <h6>Events in period</h6>
          <b-table :items="summary.per_event" :fields="eventFields" small striped hover responsive show-empty empty-text="No events in this period">
            <template #cell(title)="{ item }">
              <b-link :to="{ name: 'governance-event-view', params: { id: item.id } }">{{ item.title }}</b-link>
            </template>
            <template #cell(held)="{ item }">
              <feather-icon :icon="item.held ? 'CheckIcon' : 'MinusIcon'" :class="item.held ? 'text-success' : 'text-muted'" />
            </template>
          </b-table>
        </b-tab>

        <!-- Attendance by person -->
        <b-tab title="Attendance">
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
            <small class="text-muted mr-1">
              Attendance % is meetings attended out of held meetings where attendance was recorded.
              Cancelled and draft events are not counted.
            </small>
            <b-button variant="success" size="sm" @click="exportReport('attendance')">Export</b-button>
          </div>
          <b-table :items="analytics.attendance" :fields="attendanceFields" small striped hover responsive show-empty empty-text="No governance members">
            <template #cell(attendance_rate)="{ item }">
              <b-badge v-if="item.attendance_rate !== null" :variant="rateVariant(item.attendance_rate)">{{ pct(item.attendance_rate) }}</b-badge>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(not_recorded)="{ item }">
              <span :class="item.not_recorded ? 'text-warning font-weight-bold' : ''">{{ item.not_recorded }}</span>
            </template>
          </b-table>
        </b-tab>

        <!-- Trends -->
        <b-tab title="Trends">
          <b-row>
            <b-col cols="12" lg="6" class="mb-2">
              <b-card class="h-100">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <h5 class="mb-0">Events Over Time</h5>
                  <b-button size="sm" variant="success" @click="exportReport('trends')">Export</b-button>
                </div>
                <canvas ref="eventsOverTime" height="220"></canvas>
              </b-card>
            </b-col>
            <b-col cols="12" lg="6" class="mb-2">
              <b-card class="h-100">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <h5 class="mb-0">Events by Type</h5>
                  <b-button size="sm" variant="success" @click="exportReport('trends')">Export</b-button>
                </div>
                <canvas ref="eventsByType" height="220"></canvas>
                <p v-if="!analytics.trends.by_type.length" class="text-muted text-center mt-1 mb-0">No events in this period</p>
              </b-card>
            </b-col>
          </b-row>
        </b-tab>

        <!-- Response rate -->
        <b-tab title="Response Rate">
          <div class="d-flex justify-content-end mb-1">
            <b-button variant="success" size="sm" @click="exportReport('responses')">Export</b-button>
          </div>
          <b-row class="mb-2">
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Invitations" :value="analytics.responses.overall.invited" icon="MailIcon" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Accepted" :value="analytics.responses.overall.accepted" icon="CheckIcon" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Tentative" :value="analytics.responses.overall.tentative" icon="HelpCircleIcon" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Declined" :value="analytics.responses.overall.declined" icon="XIcon" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="No response" :value="analytics.responses.overall.pending" icon="ClockIcon" /></b-col>
            <b-col cols="6" md="2" class="mb-1"><kpi-tile label="Response rate" :value="pct(analytics.responses.overall.response_rate)" icon="PercentIcon" hint="Accepted, tentative or declined, out of all invitations" /></b-col>
          </b-row>
          <b-table :items="analytics.responses.per_event" :fields="responseFields" small striped hover responsive show-empty empty-text="No events in this period">
            <template #cell(title)="{ item }">
              <b-link :to="{ name: 'governance-event-view', params: { id: item.id } }">{{ item.title }}</b-link>
            </template>
            <template #cell(response_rate)="{ item }">
              <b-badge v-if="item.response_rate !== null" :variant="rateVariant(item.response_rate)">{{ pct(item.response_rate) }}</b-badge>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(acceptance_rate)="{ item }">{{ pct(item.acceptance_rate) }}</template>
          </b-table>
        </b-tab>

        <!-- Minutes compliance -->
        <b-tab title="Minutes Compliance">
          <div class="d-flex justify-content-end mb-1">
            <b-button variant="success" size="sm" @click="exportReport('compliance')">Export</b-button>
          </div>
          <b-row class="mb-2">
            <b-col cols="6" md="3" class="mb-1"><kpi-tile label="Meetings held" :value="analytics.compliance.totals.held" icon="CalendarIcon" /></b-col>
            <b-col cols="6" md="3" class="mb-1"><kpi-tile label="With agenda and minutes" :value="analytics.compliance.totals.compliant" icon="CheckCircleIcon" /></b-col>
            <b-col cols="6" md="3" class="mb-1"><kpi-tile label="Missing agenda" :value="analytics.compliance.totals.missing_agenda" icon="ListIcon" /></b-col>
            <b-col cols="6" md="3" class="mb-1"><kpi-tile label="Missing minutes" :value="analytics.compliance.totals.missing_minutes" icon="FileTextIcon" /></b-col>
          </b-row>
          <p class="text-muted small mb-1">
            Covers meetings in the Director Event Scheduler only. For each meeting already held, it checks whether an agenda and minutes were uploaded to the event.
            Minutes attached to working group updates are reported on the Working Groups tab.
          </p>
          <h6>Held meetings missing an agenda or minutes</h6>
          <b-table :items="analytics.compliance.rows" :fields="complianceFields" small striped hover responsive show-empty empty-text="Every meeting held in this period has an agenda and minutes">
            <template #cell(title)="{ item }">
              <b-link :to="{ name: 'governance-event-view', params: { id: item.id } }">{{ item.title }}</b-link>
            </template>
            <template #cell(has_agenda)="{ item }">
              <b-badge :variant="item.has_agenda ? 'light-success' : 'light-danger'">{{ item.has_agenda ? "Uploaded" : "Missing" }}</b-badge>
            </template>
            <template #cell(has_minutes)="{ item }">
              <b-badge :variant="item.has_minutes ? 'light-success' : 'light-danger'">{{ item.has_minutes ? "Uploaded" : "Missing" }}</b-badge>
            </template>
            <template #cell(attendance_recorded)="{ item }">
              <b-badge :variant="item.attendance_recorded ? 'light-success' : 'light-warning'">{{ item.attendance_recorded ? "Yes" : "No" }}</b-badge>
            </template>
          </b-table>
        </b-tab>

        <!-- Working group activity -->
        <b-tab title="Working Groups">
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
            <small class="text-muted mr-1">Working group updates whose meeting date falls in the selected period. Updates without a meeting date use the date they were entered into CPAC.</small>
            <b-button variant="success" size="sm" @click="exportReport('working-groups')">Export</b-button>
          </div>
          <b-table :items="analytics.working_groups" :fields="wgActivityFields" small striped hover responsive show-empty empty-text="No Governance working groups">
            <template #cell(last_update)="{ item }">{{ item.last_update || "-" }}</template>
          </b-table>
        </b-tab>
      </b-tabs>
    </b-card>
  </b-overlay>
</template>

<script>
import store from "@/store";
import moment from "moment";
import Chart from "chart.js";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { onUnmounted } from "@vue/composition-api";
import KpiTile from "@/views/Report/KpiTile.vue";
import governanceMixin, { registerGovernanceStore, unregisterGovernanceStore } from "./governanceMixin";

const TRENDS_TAB = 2;
const num = (key, label) => ({ key, label, class: "text-right", sortable: true });

export default {
  name: "GovernanceReports",
  components: { vSelect, flatPickr, KpiTile },
  mixins: [governanceMixin],
  setup() {
    registerGovernanceStore();
    onUnmounted(() => unregisterGovernanceStore());
  },
  data() {
    const from = moment().startOf("year").format("YYYY-MM-DD");
    const to = moment().endOf("year").format("YYYY-MM-DD");
    return {
      loading: false,
      activeTab: 0,
      summary: null,
      analytics: null,
      workingGroups: [],
      enums: { event_types: [] },
      dateRange: `${from} to ${to}`,
      rangeConfig: { mode: "range", dateFormat: "Y-m-d", altInput: true, altFormat: "d/m/Y" },
      filters: { from, to, event_type: null, working_group_id: null },
      typeFields: [{ key: "label", label: "Type" }, num("count", "Events"), num("held", "Held"), num("cancelled", "Cancelled")],
      monthFields: [{ key: "label", label: "Month" }, num("count", "Events"), num("held", "Held"), num("cancelled", "Cancelled")],
      wgFields: [{ key: "working_group", label: "Working group" }, num("count", "Events"), num("held", "Held"), num("cancelled", "Cancelled")],
      eventFields: [
        { key: "date", label: "Date" },
        { key: "start_time", label: "Time" },
        { key: "title", label: "Title" },
        { key: "type", label: "Type" },
        { key: "working_group", label: "Working group" },
        { key: "status", label: "Status" },
        { key: "held", label: "Held", class: "text-center" },
        num("invited", "Invited"),
        num("accepted", "Accepted"),
        num("declined", "Declined"),
        num("attended", "Attended"),
      ],
      attendanceFields: [
        { key: "name", label: "Name", sortable: true },
        { key: "role", label: "Role", sortable: true },
        num("invited", "Invited"),
        num("held", "Held"),
        num("accepted", "Accepted"),
        num("declined", "Declined"),
        num("pending", "No response"),
        num("attended", "Attended"),
        num("absent", "Absent"),
        num("not_recorded", "Not recorded"),
        { key: "attendance_rate", label: "Attendance %", class: "text-center", sortable: true },
      ],
      responseFields: [
        { key: "date", label: "Date" },
        { key: "title", label: "Event", sortable: true },
        { key: "type", label: "Type" },
        { key: "status", label: "Status" },
        num("invited", "Invited"),
        num("accepted", "Accepted"),
        num("tentative", "Tentative"),
        num("declined", "Declined"),
        num("pending", "No response"),
        { key: "response_rate", label: "Response rate", class: "text-center", sortable: true },
        { key: "acceptance_rate", label: "Acceptance rate", class: "text-right", sortable: true },
      ],
      complianceFields: [
        { key: "date", label: "Date" },
        { key: "title", label: "Event" },
        { key: "type", label: "Type" },
        { key: "working_group", label: "Working group" },
        { key: "status", label: "Status" },
        { key: "has_agenda", label: "Agenda", class: "text-center" },
        { key: "has_minutes", label: "Minutes", class: "text-center" },
        { key: "attendance_recorded", label: "Attendance recorded", class: "text-center" },
      ],
      wgActivityFields: [
        { key: "working_group", label: "Working group", sortable: true },
        num("members", "Members"),
        num("updates", "Updates"),
        num("updates_with_minutes", "Updates with minutes"),
        { key: "last_update", label: "Last meeting date" },
        num("events", "Events in period"),
        num("events_held", "Events held"),
      ],
    };
  },
  computed: {
    typeOptions() {
      return (this.enums.event_types.length ? this.enums.event_types : Object.keys(this.governanceTypeLabels))
        .map((t) => ({ value: t, label: this.govTypeLabel(t) }));
    },
    params() {
      const p = {};
      Object.keys(this.filters).forEach((k) => {
        if (this.filters[k] !== null && this.filters[k] !== "") p[k] = this.filters[k];
      });
      return p;
    },
  },
  watch: {
    activeTab(tab) {
      if (tab === TRENDS_TAB) this.$nextTick(this.renderCharts);
    },
  },
  created() {
    // Chart instances are kept off the reactive data so Vue does not observe them.
    this.charts = { overTime: null, byType: null };
  },
  mounted() {
    store.dispatch("governance/fetchOptions").then((res) => {
      this.workingGroups = res.data.workingGroups || [];
      this.enums = res.data.enums || this.enums;
    });
    this.load();
  },
  beforeDestroy() {
    this.destroyCharts();
  },
  methods: {
    pct(v) {
      return v === null || v === undefined ? "-" : `${v}%`;
    },
    rateVariant(v) {
      if (v >= 75) return "light-success";
      if (v >= 50) return "light-warning";
      return "light-danger";
    },
    onRangeChange(selectedDates) {
      if (selectedDates.length === 2) {
        this.filters.from = moment(selectedDates[0]).format("YYYY-MM-DD");
        this.filters.to = moment(selectedDates[1]).format("YYYY-MM-DD");
        this.load();
      }
    },
    load() {
      this.loading = true;
      Promise.all([
        store.dispatch("governance/fetchReportSummary", this.params),
        store.dispatch("governance/fetchReportAnalytics", this.params),
      ])
        .then(([summary, analytics]) => {
          this.summary = summary.data;
          this.analytics = analytics.data;
          if (this.activeTab === TRENDS_TAB) this.$nextTick(this.renderCharts);
        })
        .catch((error) => this.govToast("Could not load report", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.loading = false;
        });
    },
    destroyCharts() {
      Object.keys(this.charts).forEach((k) => {
        if (this.charts[k]) this.charts[k].destroy();
        this.charts[k] = null;
      });
    },
    renderCharts() {
      if (!this.analytics || !this.$refs.eventsOverTime || !this.$refs.eventsByType) return;
      this.destroyCharts();
      const months = this.analytics.trends.by_month;
      const yAxes = [{ ticks: { beginAtZero: true, precision: 0 } }];

      this.charts.overTime = new Chart(this.$refs.eventsOverTime.getContext("2d"), {
        type: "line",
        data: {
          labels: months.map((m) => m.label),
          datasets: [
            { label: "Events", data: months.map((m) => m.count), borderColor: "#42A5F5", fill: false },
            { label: "Held", data: months.map((m) => m.held), borderColor: "#28C76F", fill: false },
            { label: "Cancelled", data: months.map((m) => m.cancelled), borderColor: "#EA5455", fill: false, borderDash: [4, 4] },
          ],
        },
        // Straight segments: curved lines dip below zero between months.
        options: { elements: { line: { tension: 0 } }, scales: { yAxes } },
      });

      const types = this.analytics.trends.by_type;
      this.charts.byType = new Chart(this.$refs.eventsByType.getContext("2d"), {
        type: "bar",
        data: {
          labels: types.map((t) => t.label),
          datasets: [{ label: "Events", data: types.map((t) => t.count), backgroundColor: "#7367F0" }],
        },
        options: { legend: { display: false }, scales: { yAxes } },
      });
    },
    exportReport(report) {
      this.loading = true;
      store
        .dispatch("governance/exportReport", { report, ...this.params })
        .then((res) => this.govSaveBlob(res.data, `governance_${report}.xlsx`))
        .catch((error) => this.govToast("Export failed", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.loading = false;
        });
    },
    exportFile(action, fileName) {
      this.loading = true;
      store
        .dispatch(`governance/${action}`, this.params)
        .then((res) => this.govSaveBlob(res.data, fileName))
        .catch((error) => this.govToast("Export failed", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
.governance-reports {
  --accent: #7367f0;
  --accent-soft: rgba(115, 103, 240, 0.12);
}
</style>
